import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-1861">
      <div className="flex justify-between items-center print:hidden" data-id="element-1862">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1863">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-1864">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1865">
            <div className="relative max-w-md" data-id="element-1866">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1867">
                <Search className="h-5 w-5 text-gray-400" data-id="element-1868" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1869" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-1870">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1871">
                  <div className="flex items-center" data-id="element-1872">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-1873">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-1874" />
                    </div>
                    <div data-id="element-1875">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1876">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1877">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1878">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-1879">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1880">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-1881">
                      <FileText className="w-4 h-4 mr-2" data-id="element-1882" /> View File
                    </Button>
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-1883">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1884" />
                <p className="text-lg font-medium text-gray-900" data-id="element-1885">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-1886">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-1887">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-1888">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1889">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-1890">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-1891">
                  <Printer className="w-4 h-4 mr-1" data-id="element-1892" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-1893">
                  <Download className="w-4 h-4 mr-1" data-id="element-1894" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-1895">
                  <X className="w-5 h-5 text-gray-500" data-id="element-1896" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-1897">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-1898">
                <div className="flex items-center" data-id="element-1899">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-1900" />
                  <div data-id="element-1901">
                    <h1 className="text-xl font-bold uppercase" data-id="element-1902">
                      BMED Medical Centre
                    </h1>
                    <p className="text-xs italic" data-id="element-1903">Maternity Care Made Better</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-1904">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-1905">
                    Maternity Record
                  </h2>
                  <p data-id="element-1906">
                    <span className="font-semibold" data-id="element-1907">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-1908">
                    <span className="font-semibold" data-id="element-1909">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-1910">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-1911">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-1912">
                  <div data-id="element-1913">
                    <span className="font-semibold" data-id="element-1914">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-1915">
                    <span className="font-semibold" data-id="element-1916">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-1917">
                    <span className="font-semibold" data-id="element-1918">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-1919">
                    <span className="font-semibold" data-id="element-1920">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-1921">
                    <span className="font-semibold" data-id="element-1922">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-1923">
                    <span className="font-semibold" data-id="element-1924">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-1925">
                    <span className="font-semibold" data-id="element-1926">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-1927">
                    <span className="font-semibold" data-id="element-1928">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-1929">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-1930">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-1931">
                  <div data-id="element-1932">
                    <span className="font-semibold" data-id="element-1933">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-1934">
                    <span className="font-semibold" data-id="element-1935">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-1936">
                    <span className="font-semibold" data-id="element-1937">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-1938">
                    <span className="font-semibold" data-id="element-1939">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-1940">
                    <span className="font-semibold" data-id="element-1941">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-1942">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-1943">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-1944">
                  <div data-id="element-1945">
                    <span className="font-semibold" data-id="element-1946">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-1947">
                    <span className="font-semibold" data-id="element-1948">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-1949">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-1950">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-1951">
                  <div data-id="element-1952">
                    <span className="font-semibold" data-id="element-1953">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-1954">
                    <div data-id="element-1955">
                      <span className="font-semibold" data-id="element-1956">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-1957">
                      <span className="font-semibold" data-id="element-1958">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-1959">
                      <span className="font-semibold" data-id="element-1960">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-1961">
                      <span className="font-semibold" data-id="element-1962">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-1963">
                    <div data-id="element-1964">
                      <span className="font-semibold" data-id="element-1965">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-1966">
                      <span className="font-semibold" data-id="element-1967">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-1968">
                    <span className="font-semibold" data-id="element-1969">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-1970">
                    <span className="font-semibold" data-id="element-1971">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-1972">
                    <div data-id="element-1973">
                      <span className="font-semibold" data-id="element-1974">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-1975">
                      <span className="font-semibold" data-id="element-1976">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-1977">
                      <span className="font-semibold" data-id="element-1978">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-1979">
                    <span className="font-semibold" data-id="element-1980">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-1981">
                    <div data-id="element-1982">
                      <span className="font-semibold" data-id="element-1983">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-1984">
                      <span className="font-semibold" data-id="element-1985">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-1986">
                      <span className="font-semibold" data-id="element-1987">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-1988">
                      <span className="font-semibold" data-id="element-1989">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-1990">
                    <div data-id="element-1991">
                      <span className="font-semibold" data-id="element-1992">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-1993">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-1994">
                      <span className="font-semibold" data-id="element-1995">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-1996">
                      <span className="font-semibold" data-id="element-1997">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-1998">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-1999">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-2000">
                  <div className="flex space-x-8" data-id="element-2001">
                    <div data-id="element-2002">
                      <span className="font-semibold" data-id="element-2003">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-2004">
                      <span className="font-semibold" data-id="element-2005">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-2006">
                      <span className="font-semibold" data-id="element-2007">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-2008">
                      <span className="font-semibold" data-id="element-2009">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2010">
                    <div data-id="element-2011">
                      <span className="font-semibold" data-id="element-2012">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-2013">
                      <span className="font-semibold" data-id="element-2014">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2015">
                    <div data-id="element-2016">
                      <span className="font-semibold" data-id="element-2017">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-2018">
                      <span className="font-semibold" data-id="element-2019">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-2020">
                      <span className="font-semibold" data-id="element-2021">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2022">
                    <div data-id="element-2023">
                      <span className="font-semibold" data-id="element-2024">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-2025">
                      <span className="font-semibold" data-id="element-2026">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-2027">
                    <span className="font-semibold" data-id="element-2028">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-2029">
                    <span className="font-semibold" data-id="element-2030">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2031">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-2032">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-2033">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-2034">
                  <thead data-id="element-2035">
                    <tr className="bg-gray-100" data-id="element-2036">
                      <th className="border border-black p-1" data-id="element-2037">Date</th>
                      <th className="border border-black p-1" data-id="element-2038">Time</th>
                      <th className="border border-black p-1" data-id="element-2039">Pulse</th>
                      <th className="border border-black p-1" data-id="element-2040">BP</th>
                      <th className="border border-black p-1" data-id="element-2041">FH</th>
                      <th className="border border-black p-1" data-id="element-2042">Temp</th>
                      <th className="border border-black p-1" data-id="element-2043">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-2044">Contractions</th>
                      <th className="border border-black p-1" data-id="element-2045">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-2046">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-2047">Remarks</th>
                      <th className="border border-black p-1" data-id="element-2048">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2049">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-2050">
                          <td className="border border-black p-1" data-id="element-2051">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-2052">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-2053">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-2054">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-2055">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-2056">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-2057">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-2058">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-2059">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-2060">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-2061">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-2062">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-2063">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-2064">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-2065">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2066">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2067">
                  <div data-id="element-2068">
                    <span className="font-semibold" data-id="element-2069">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-2070">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-2071">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-2072">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-2073">
                    <span className="font-semibold" data-id="element-2074">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-2075">
                    <span className="font-semibold" data-id="element-2076">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-2077">
                    <span className="font-semibold" data-id="element-2078">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2079">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-2080">
                    <span className="font-semibold" data-id="element-2081">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-2082">
                    <span className="font-semibold" data-id="element-2083">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-2084">
                    <span className="font-semibold" data-id="element-2085">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-2086">
                    <span className="font-semibold" data-id="element-2087">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-2088">
                    <div data-id="element-2089">
                      <span className="font-semibold" data-id="element-2090">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-2091">
                      <span className="font-semibold" data-id="element-2092">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-2093">
                      <span className="font-semibold" data-id="element-2094">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-2095">
                      <span className="font-semibold" data-id="element-2096">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-2097">
                    <span className="font-semibold" data-id="element-2098">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2099">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-2100">
                    <span className="font-semibold" data-id="element-2101">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-2102">
                    <span className="font-semibold" data-id="element-2103">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2104">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-2105">
                    <span className="font-semibold" data-id="element-2106">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2107">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-2108">
                    <span className="font-semibold" data-id="element-2109">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-2110">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-2111">
                    <span className="font-semibold" data-id="element-2112">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-2113">
                    <span className="font-semibold" data-id="element-2114">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-2115">
                    <span className="font-semibold" data-id="element-2116">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-2117">
                    <span className="font-semibold" data-id="element-2118">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-2119">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-2120">
                    <span className="font-semibold" data-id="element-2121">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-2122">
                  <h4 className="font-bold text-sm mb-1" data-id="element-2123">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-2124">
                    <thead data-id="element-2125">
                      <tr className="bg-gray-100" data-id="element-2126">
                        <th className="border border-black p-1" data-id="element-2127">DATE</th>
                        <th className="border border-black p-1" data-id="element-2128">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-2129">SEX</th>
                        <th className="border border-black p-1" data-id="element-2130">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-2131">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2132">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-2133">
                            <td className="border border-black p-1" data-id="element-2134">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-2135">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-2136">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-2137">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-2138">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-2139">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-2140">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2141">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2142">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2143">
                  <div data-id="element-2144">
                    <span className="font-semibold" data-id="element-2145">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-2146">
                    <span className="font-semibold" data-id="element-2147">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-2148">
                    <span className="font-semibold" data-id="element-2149">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-2150">
                    <span className="font-semibold" data-id="element-2151">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-2152">
                    <div data-id="element-2153">
                      <span className="font-semibold" data-id="element-2154">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-2155">
                      <span className="font-semibold" data-id="element-2156">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-2157">
                      <span className="font-semibold" data-id="element-2158">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-2159">
                    <span className="font-semibold" data-id="element-2160">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-2161">
                    <span className="font-semibold" data-id="element-2162">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-2163">
                    <span className="font-semibold" data-id="element-2164">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-2165">
                    <span className="font-semibold" data-id="element-2166">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-2167">
                    <span className="font-semibold" data-id="element-2168">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-2169">
                    <span className="font-semibold" data-id="element-2170">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-2171">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-2172">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2173">
                    <div data-id="element-2174">
                      <span className="font-semibold" data-id="element-2175">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-2176">
                      <span className="font-semibold" data-id="element-2177">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-2178">
                      <span className="font-semibold" data-id="element-2179">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-2180">
                      <span className="font-semibold" data-id="element-2181">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-2182">
                      <span className="font-semibold" data-id="element-2183">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-2184">
                      <span className="font-semibold" data-id="element-2185">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-2186">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-2187">
                      <span className="font-semibold" data-id="element-2188">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-2189">
                      <span className="font-semibold" data-id="element-2190">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-2191">
                      <span className="font-semibold" data-id="element-2192">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-2193">
                      <span className="font-semibold" data-id="element-2194">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-2195">
                      <span className="font-semibold" data-id="element-2196">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-2197">
                      <span className="font-semibold" data-id="element-2198">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-2199">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-2200">
                      <span className="font-semibold" data-id="element-2201">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-2202">
                      <span className="font-semibold" data-id="element-2203">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-2204">
                      <span className="font-semibold" data-id="element-2205">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-2206">
                      <span className="font-semibold" data-id="element-2207">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-2208">
                      <span className="font-semibold" data-id="element-2209">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-2210">
                      <span className="font-semibold" data-id="element-2211">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-2212">
                      <span className="font-semibold" data-id="element-2213">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-2214">
                      <span className="font-semibold" data-id="element-2215">Reflexes:</span>
                      <div data-id="element-2216">
                        <span className="font-semibold" data-id="element-2217">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-2218">
                        <span className="font-semibold" data-id="element-2219">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-2220">
                        <span className="font-semibold" data-id="element-2221">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-2222">
                  <span className="font-semibold" data-id="element-2223">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-2224">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2225">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2226">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-2227">
                  <thead data-id="element-2228">
                    <tr className="bg-gray-100" data-id="element-2229">
                      <th className="border border-black p-1" colSpan={2} data-id="element-2230">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-2231">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-2232">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-2233">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-2234">
                      <th className="border border-black p-1" data-id="element-2235">DATE</th>
                      <th className="border border-black p-1" data-id="element-2236">TIME</th>
                      <th className="border border-black p-1" data-id="element-2237">BP</th>
                      <th className="border border-black p-1" data-id="element-2238">PULSE</th>
                      <th className="border border-black p-1" data-id="element-2239">RESPS</th>
                      <th className="border border-black p-1" data-id="element-2240">TEMP</th>
                      <th className="border border-black p-1" data-id="element-2241">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-2242">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-2243">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-2244">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-2245">TEMP</th>
                      <th className="border border-black p-1" data-id="element-2246">RESPS</th>
                      <th className="border border-black p-1" data-id="element-2247">CORD</th>
                      <th className="border border-black p-1" data-id="element-2248">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-2249">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-2250">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-2251">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2252">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-2253">
                          <td className="border border-black p-1" data-id="element-2254">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-2255">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-2256">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-2257">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-2258">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-2259">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-2260">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-2261">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-2262">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-2263">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-2264">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-2265">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-2266">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-2267">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-2268">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-2269">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-2270">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-2271">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-2272">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-2273">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2274">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-2275">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-2276">
                  <div data-id="element-2277">
                    <span className="font-semibold" data-id="element-2278">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-2279">
                    <span className="font-semibold" data-id="element-2280">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-2281">
                    <span className="font-semibold" data-id="element-2282">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-2283">
                    <span className="font-semibold" data-id="element-2284">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-2285">
                  <thead data-id="element-2286">
                    <tr className="bg-gray-100" data-id="element-2287">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-2288">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-2289">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-2290">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-2291">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-2292">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-2293">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-2294">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-2295">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-2296">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-2297">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-2298">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-2299">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-2300">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-2301">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-2302">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-2303">
                          <td className="border border-black p-1" data-id="element-2304">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-2305">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-2306">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-2307">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-2308">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-2309">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-2310">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-2311">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-2312">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-2313">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-2314">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-2315">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-2316">
                  <div data-id="element-2317">
                    <span className="font-semibold" data-id="element-2318">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-2319">
                    <span className="font-semibold" data-id="element-2320">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2321">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2322">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-2323">
                  <thead data-id="element-2324">
                    <tr className="bg-gray-100" data-id="element-2325">
                      <th className="border border-black p-2 text-left w-32" data-id="element-2326">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-2327">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-2328">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2329">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-2330">
                          <td className="border border-black p-2" data-id="element-2331">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-2332">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-2333">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-2334">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-2335">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2336">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-2337">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-2338">
                  <div data-id="element-2339">
                    <span className="font-semibold" data-id="element-2340">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-2341">
                    <span className="font-semibold" data-id="element-2342">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-2343">
                    <span className="font-semibold" data-id="element-2344">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-2345">
                    <span className="font-semibold" data-id="element-2346">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-2347">
                    <span className="font-semibold" data-id="element-2348">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-2349">
                    <span className="font-semibold" data-id="element-2350">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2351">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-2352">
                  <div className="font-bold uppercase" data-id="element-2353">Mother</div>
                  <div className="font-bold uppercase" data-id="element-2354">Infant</div>
                  <div data-id="element-2355">
                    <span className="font-semibold" data-id="element-2356">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-2357">
                    <span className="font-semibold" data-id="element-2358">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-2359">
                    <span className="font-semibold" data-id="element-2360">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-2361">
                    <span className="font-semibold" data-id="element-2362">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-2363">
                    <span className="font-semibold" data-id="element-2364">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-2365">
                    <span className="font-semibold" data-id="element-2366">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-2367">
                    <span className="font-semibold" data-id="element-2368">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-2369">
                    <span className="font-semibold" data-id="element-2370">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-2371">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-2372">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-2373">
                    <span className="font-semibold" data-id="element-2374">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-2375">
                  <h5 className="font-bold uppercase mb-2" data-id="element-2376">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2377">
                    <div data-id="element-2378">
                      <span className="font-semibold" data-id="element-2379">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-2380">
                      <span className="font-semibold" data-id="element-2381">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-2382">
                      <span className="font-semibold" data-id="element-2383">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-2384">
                      <span className="font-semibold" data-id="element-2385">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-2386">
                      <span className="font-semibold" data-id="element-2387">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-2388">
                      <span className="font-semibold" data-id="element-2389">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-2390">
                      <span className="font-semibold" data-id="element-2391">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-2392">
                      <span className="font-semibold" data-id="element-2393">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-2394">
                      <span className="font-semibold" data-id="element-2395">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-2396">
                      <span className="font-semibold" data-id="element-2397">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-2398">
                      <span className="font-semibold" data-id="element-2399">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-2400">
                      <span className="font-semibold" data-id="element-2401">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-2402">
                      <span className="font-semibold" data-id="element-2403">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-2404">
                  <h5 className="font-bold uppercase mb-2" data-id="element-2405">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2406">
                    <div data-id="element-2407">
                      <span className="font-semibold" data-id="element-2408">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-2409">
                      <span className="font-semibold" data-id="element-2410">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-2411">
                      <span className="font-semibold" data-id="element-2412">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-2413">
                      <span className="font-semibold" data-id="element-2414">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-2415">
                      <span className="font-semibold" data-id="element-2416">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-2417">
                      <span className="font-semibold" data-id="element-2418">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-2419">
                      <span className="font-semibold" data-id="element-2420">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-2421">
                      <span className="font-semibold" data-id="element-2422">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-2423">
                      <span className="font-semibold" data-id="element-2424">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-2425">
                      <span className="font-semibold" data-id="element-2426">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-2427">
                      <span className="font-semibold" data-id="element-2428">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-2429">
                      <span className="font-semibold" data-id="element-2430">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-2431">
                      <span className="font-semibold" data-id="element-2432">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-2433">
                  <div data-id="element-2434">
                    <span className="font-semibold" data-id="element-2435">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-2436">
                    <span className="font-semibold" data-id="element-2437">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-2438">
                    <span className="font-semibold" data-id="element-2439">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2440">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-2441">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-2442">
                  <span className="font-semibold" data-id="element-2443">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2444">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-2445">
                  <div data-id="element-2446">
                    <span className="font-semibold" data-id="element-2447">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-2448">
                    <span className="font-semibold" data-id="element-2449">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-2450">
                    <span className="font-semibold" data-id="element-2451">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-2452">
                    <span className="font-semibold" data-id="element-2453">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-2454">
                    <span className="font-semibold" data-id="element-2455">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-2456">
                    <span className="font-semibold" data-id="element-2457">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-2458">
                    <span className="font-semibold" data-id="element-2459">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-2460">
                    <span className="font-semibold" data-id="element-2461">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-2462">
                    <span className="font-semibold" data-id="element-2463">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-2464">
                    <span className="font-semibold" data-id="element-2465">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-2466">
                    <span className="font-semibold" data-id="element-2467">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-2468">
                    <span className="font-semibold" data-id="element-2469">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-2470">
                    <span className="font-semibold" data-id="element-2471">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-2472">
                    <span className="font-semibold" data-id="element-2473">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-2474">
                    <span className="font-semibold" data-id="element-2475">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-2476">
                    <span className="font-semibold" data-id="element-2477">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-2478">
                    <span className="font-semibold" data-id="element-2479">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-2480">
                    <span className="font-semibold" data-id="element-2481">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2482">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-2483">
                  <div data-id="element-2484">
                    <span className="font-semibold" data-id="element-2485">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-2486">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-2487">
                    <span className="font-semibold" data-id="element-2488">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-2489">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-2490">
                    <span className="font-semibold" data-id="element-2491">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-2492">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2493">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-2494">
                  <div data-id="element-2495">
                    <span className="font-semibold" data-id="element-2496">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-2497">
                    <span className="font-semibold" data-id="element-2498">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-2499">
                    <span className="font-semibold" data-id="element-2500">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-2501">
                  <div data-id="element-2502">
                    <span className="font-semibold" data-id="element-2503">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-2504">
                    <span className="font-semibold" data-id="element-2505">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-2506">
                    <span className="font-semibold" data-id="element-2507">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-2508">
                    <span className="font-semibold" data-id="element-2509">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-2510">
                    <span className="font-semibold" data-id="element-2511">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-2512">
                    <span className="font-semibold" data-id="element-2513">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-2514">
                    <span className="font-semibold" data-id="element-2515">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-2516">
                    <span className="font-semibold" data-id="element-2517">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-2518">
                    <span className="font-semibold" data-id="element-2519">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-2520">
                    <span className="font-semibold" data-id="element-2521">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-2522">
                    <span className="font-semibold" data-id="element-2523">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-2524">
                    <span className="font-semibold" data-id="element-2525">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-2526">
                    <span className="font-semibold" data-id="element-2527">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-2528">
                    <span className="font-semibold" data-id="element-2529">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-2530">
                    <span className="font-semibold" data-id="element-2531">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-2532">
                    <span className="font-semibold" data-id="element-2533">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-2534">
                  <span className="font-semibold" data-id="element-2535">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-2536">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2537">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-2538">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-2539">
                  <span className="font-semibold" data-id="element-2540">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2541">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-2542">
                  <div data-id="element-2543">
                    <span className="font-semibold" data-id="element-2544">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-2545">
                    <span className="font-semibold" data-id="element-2546">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-2547">
                    <span className="font-semibold" data-id="element-2548">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-2549">
                    <span className="font-semibold" data-id="element-2550">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-2551">
                    <span className="font-semibold" data-id="element-2552">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-2553">
                    <span className="font-semibold" data-id="element-2554">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-2555">
                    <span className="font-semibold" data-id="element-2556">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-2557">
                    <span className="font-semibold" data-id="element-2558">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-2559">
                    <span className="font-semibold" data-id="element-2560">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-2561">
                    <span className="font-semibold" data-id="element-2562">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-2563">
                    <span className="font-semibold" data-id="element-2564">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-2565">
                    <span className="font-semibold" data-id="element-2566">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-2567">
                    <span className="font-semibold" data-id="element-2568">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-2569">
                    <span className="font-semibold" data-id="element-2570">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-2571">
                    <span className="font-semibold" data-id="element-2572">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-2573">
                    <span className="font-semibold" data-id="element-2574">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-2575">
                    <span className="font-semibold" data-id="element-2576">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-2577">
                    <span className="font-semibold" data-id="element-2578">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2579">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-2580">
                  <div data-id="element-2581">
                    <span className="font-semibold" data-id="element-2582">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-2583">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-2584">
                    <span className="font-semibold" data-id="element-2585">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-2586">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-2587">
                    <span className="font-semibold" data-id="element-2588">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-2589">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-2590">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-2591">
                  <div data-id="element-2592">
                    <span className="font-semibold" data-id="element-2593">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-2594">
                    <span className="font-semibold" data-id="element-2595">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-2596">
                    <span className="font-semibold" data-id="element-2597">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-2598">
                  <div data-id="element-2599">
                    <span className="font-semibold" data-id="element-2600">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-2601">
                    <span className="font-semibold" data-id="element-2602">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-2603">
                    <span className="font-semibold" data-id="element-2604">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-2605">
                    <span className="font-semibold" data-id="element-2606">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-2607">
                    <span className="font-semibold" data-id="element-2608">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-2609">
                    <span className="font-semibold" data-id="element-2610">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-2611">
                    <span className="font-semibold" data-id="element-2612">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-2613">
                    <span className="font-semibold" data-id="element-2614">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-2615">
                    <span className="font-semibold" data-id="element-2616">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-2617">
                    <span className="font-semibold" data-id="element-2618">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-2619">
                    <span className="font-semibold" data-id="element-2620">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-2621">
                    <span className="font-semibold" data-id="element-2622">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-2623">
                    <span className="font-semibold" data-id="element-2624">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-2625">
                    <span className="font-semibold" data-id="element-2626">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-2627">
                    <span className="font-semibold" data-id="element-2628">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-2629">
                    <span className="font-semibold" data-id="element-2630">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-2631">
                  <span className="font-semibold" data-id="element-2632">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-2633">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};